import { Injectable } from '@angular/core';
import {
  DocumentResponse,
  ListResponse,
  SuggestionListRequest,
  ApiMenu,
  SuggestionResponse,
  SuggestionRequest,
} from '../interfaces/menu-info.interface';
import { AuthService } from './auth.service';
import { map, Observable, shareReplay } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiResponse } from '../interfaces/common.interfaces';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class MenuInfoService {
  private apiUrl = environment.int365_dom + '/api/v1';
  private apiUrl2 = environment.int365_dom + '/api/v2';
  private cachedDocuments: Observable<ApiResponse<DocumentResponse>> | null =
    null;
  private lastFetch: number = 0;
  private readonly CACHE_TIME = 30 * 60 * 1000; // 30 minutos en milisegundos
  private cachedLists: Observable<ApiMenu<ListResponse>> | null = null;
  private lastListsFetch: number = 0;

  constructor(private http: HttpClient, private authService: AuthService) {}

  // Documentos
  getDocuments(): Observable<ApiResponse<DocumentResponse>> {
    const now = Date.now();
    if (!this.cachedDocuments || now - this.lastFetch > this.CACHE_TIME) {
      this.lastFetch = now;
      this.cachedDocuments = this.http
        .get<ApiResponse<DocumentResponse>>(
          `${this.apiUrl}/Dashboard/documents`,
          this.getHttpOptions()
        )
        .pipe(
          map((response) => {
            try {
              return response;
            } catch (error) {
              console.error('Error en la obtención de documentos:', error);
              if (response.statusCode === 404) {
                throw { status: 404, error: response };
              }

              throw error;
            }
          }),
          shareReplay(1)
        );
    }
    return this.cachedDocuments;
  }

  forceRefresh(): Observable<ApiResponse<DocumentResponse>> {
    this.cachedDocuments = null;
    this.lastFetch = 0;
    return this.getDocuments();
  }

  /*Buzón de sugerencias*/
  getLists(request: SuggestionListRequest): Observable<ApiMenu<ListResponse>> {
    const now = Date.now();
    if (!this.cachedLists || now - this.lastListsFetch > this.CACHE_TIME) {
      this.lastListsFetch = now;
      this.cachedLists = this.http
        .get<ApiMenu<ListResponse>>(
          `${this.apiUrl}/Customer/Lists`,
          this.getHttpOptions(request)
        )
        .pipe(
          map((response) => {
            try {
              return response;
            } catch (error) {
              console.error('Error al obtener las listas:', error);
              if (response.StatusRequest === '404 Not Found') {
                window.alert('Error 404: No se encontró la página');
                throw { status: 404, error: response };
              }

              throw error;
            }
          }),
          shareReplay(1)
        );
    }
    return this.cachedLists;
  }

  // Método para forzar la actualización del caché de listas
  forceListsRefresh(
    request: SuggestionListRequest
  ): Observable<ApiMenu<ListResponse>> {
    this.cachedLists = null;
    this.lastListsFetch = 0;
    return this.getLists(request);
  }

  /*Enviar sugerencia*/
  sendSuggestion(
    request: SuggestionRequest
  ): Observable<ApiMenu<SuggestionResponse>> {
    return this.http
      .post<ApiMenu<SuggestionResponse>>(
        `${this.apiUrl2}/suggestions`,
        request,
        this.getHttpOptions()
      )
      .pipe(
        map((response) => {
          try {
            return response;
          } catch (error) {
            console.error('Error al enviar la sugerencia:', error);
            if (response.StatusRequest === '404 Not Found') {
              throw { status: 404, error: response };
            }
            throw error;
          }
        })
      );
  }

  // Autenticación
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  // Formato de parámetros
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }
}
